import {
	EditAddModificationGlobalAttributesWrapper,
	EditAddModificationWrapper,
	EditConfigurationSelect,
	EditLineItemHeaderContainer,
	EditLineItemHeaderRow
} from "./EditLineItemStyles.ts";
import {
	DimensionContainer,
	RequiredCategoryWrapper,
	RequiredDimensionsValues,
	RequiredSpecificationsWrapper
} from "../RequiredDimensions/RequiredDimensionsStyles.ts";
import {
	ChangeGlobalAttributeGroup,
	EditLineItemHeader,
	EditLineNumberPlaceholder,
	EditProductLineLabel
} from "./constants.ts";
import Header from "../../Common/LineItemGrid/Header/Header.tsx";
import {
	AddLineCancelButton,
	AddLineDepthLabel,
	AddLineHeightLabel,
	AddLineItemQuantityPlaceholder,
	AddLineItemStandardDimensionsHeader,
	AddLineSaveButton,
	AddLineWidthLabel,
	RequiredDimensionError,
	SelectRequiredSpecifications
} from "../AddLineItem/constants.ts";
import AutoComplete from "../../Common/Autocomplete/Autocomplete.tsx";
import { ChangeEvent, FormEventHandler, MutableRefObject, SyntheticEvent, useEffect, useState } from "react";
import {
	AddLineItemButtonWrapper,
	AddLineItemStandardContainer,
	AddLineItemStandardDimensions
} from "../AddLineItem/AddLineItemStyles.ts";
import { Button, FormControl, MenuItem, Select, Skeleton, TextField } from "@mui/material";
import AddModification from "../AddModification/AddModification.tsx";
import { models } from "types/api/viewModels.ts";
import {
	calculateIntervals,
	ConfigurationGroup,
	getAttributes,
	getSubheaderAttributes,
	isDimensionValid
} from "components/NewOrders/utils/NewOrderUtils.tsx";
import styles from "pages/replacements-page-styles.module.css";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FormSchema, schema } from "./schema.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import useAddModification from "components/NewOrders/AddModification/hooks/useAddModification.ts";
import { newOrderApiSlice } from "features/api/newOrderApi.ts";
import { ModificationSkeletonContainer } from "components/NewOrders/AddModification/AddModificationStyles.ts";
import { CustomText, EditedText } from "constants/text.ts";
import {
	EditedChip,
	LineItemErrorMessage,
	LineItemErrorMessageArr
} from "components/Common/LineItemGrid/LineItemGridStyles.ts";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import Check from "@mui/icons-material/Check";
import AddColorMatch from "../AddColorMatch/AddColorMatch";
import { AddColorMatchSchema, addColorMatchSchema } from "../AddLineItem/schema";
import { useGetPaintVendorsQuery } from "features/api/globalsApi.ts";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { useDispatch, useSelector } from "react-redux";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { RootState } from "stores/application.store.tsx";
import { ReplacementReasonAndProblemInputLabel } from "components/Replacements/components/SelectReplacementReasonAndProblemDropdowns/SelectReplacementReasonAndProblemDropdowns.styles.ts";

interface EditLineItemProps {
	group: ConfigurationGroup;
	configuration: models["PendingOrderConfigurationViewModel"];
	selectedEditItem: MutableRefObject<models["PendingLineItemViewModel"] | undefined>;
	description: string | null | undefined;
	editLineItemIndex: number | undefined;
	isAddLineItem?: boolean;
	error: string | string[] | null | undefined;
	setLineItemErrorAsEdited: (lineItemId: string) => void;
	lineItemId: string;
	isEditing: boolean;

	addEditVisibility(index?: number): void;

	handleMoveLineItem(
		item: models["PendingLineItemViewModel"],
		deleteItemIndex: number | undefined,
		itemDestinationIndex: number,
		deleteConfigId: string | null | undefined,
		destinationConfigId: string | null | undefined
	): void;
}

const EditLineItem = ({
	group,
	configuration,
	addEditVisibility,
	selectedEditItem,
	description,
	editLineItemIndex,
	handleMoveLineItem,
	isAddLineItem,
	error,
	setLineItemErrorAsEdited,
	lineItemId,
	isEditing
}: EditLineItemProps) => {
	const defaultConfigurationId = configuration?.configurationId;

	const { data: products, isLoading: isProductQueryFetching } = useSelector((state: RootState) =>
		newOrderApiSlice.endpoints.getProducts.select({
			productLineId: configuration.globals?.productLine?.id ?? "",
			sampleProducts: false
		})(state)
	);
	const productData = products?.filter((product) => product.id === selectedEditItem?.current?.id);
	const hasRequiredCategories =
		(selectedEditItem?.current?.requiredCategories &&
			selectedEditItem?.current?.requiredCategories?.length !== 0) ||
		(productData?.[0]?.requiredCategories && productData?.[0]?.requiredCategories?.length !== 0);
	const errorArr = Array.isArray(error) ? error : [error].filter(Boolean);
	const { isCustomColorPage: isCustomColor } = useUserLocation();
	const dispatch = useDispatch();

	const {
		getModificationFormErrors,
		handleSetModificationFormData,
		handleSetModificationFormErrors,
		modificationFormErrors,
		modificationFormValues,
		modifications
	} = useAddModification(selectedEditItem?.current?.id, group?.globals?.productLine?.id);
	const [requiredDimensionsValues, setRequiredDimensionsValues] = useState<models["PendingValueViewModel"][]>(
		selectedEditItem.current?.requiredDimensions ?? []
	);

	const [lineItemCount, setLineItemCount] = useState(
		configuration?.lineItems?.map(({ lineItemNumber }) => lineItemNumber) ?? []
	);

	const { handleSubmit, control, setValue } = useForm<FormSchema>({
		mode: "onChange",
		resolver: yupResolver(schema),
		reValidateMode: "onChange",
		defaultValues: {
			editItemsLineNumber: selectedEditItem?.current?.lineItemNumber,
			productSelectQuantity: selectedEditItem.current?.quantityOrdered,
			destinationConfigurationId: configuration.configurationId ?? ""
		}
	});

	const selectedAttributeGroupId = useWatch({
		control,
		name: "destinationConfigurationId"
	});

	useEffect(() => {
		let selectedAttributeGroup = group.configurations.find(
			(group) => group.configurationId === selectedAttributeGroupId
		);
		let lineNumbersOfSelectedGroup =
			selectedAttributeGroup?.lineItems?.map(({ lineItemNumber }) => lineItemNumber) ?? [];

		let selectedLinesLength = lineNumbersOfSelectedGroup.length ?? 0;

		//	if the move is in the same attribute group
		if (selectedAttributeGroup?.configurationId === defaultConfigurationId) {
			setLineItemCount(lineNumbersOfSelectedGroup ?? []);
			setValue("editItemsLineNumber", selectedEditItem?.current?.lineItemNumber ?? 0);
		} else {
			//	line item is being moved to a different group
			let lastValueOfNewAttributeGroup = (lineNumbersOfSelectedGroup?.[selectedLinesLength - 1] ?? 0) + 1;
			lineNumbersOfSelectedGroup.push(lastValueOfNewAttributeGroup);
			setLineItemCount(lineNumbersOfSelectedGroup ?? []);
			setValue("editItemsLineNumber", lastValueOfNewAttributeGroup);
		}
	}, [selectedAttributeGroupId, group.configurations, defaultConfigurationId, selectedEditItem, setValue]);

	const {
		control: requiredCategoriesControl,
		handleSubmit: requiredCategoriesHandleSubmit,
		setValue: requiredCategoriesSetValue
	} = useForm({
		mode: "onChange",
		reValidateMode: "onChange"
	});

	const {
		control: colorMatchControl,
		trigger: colorMatchTrigger,
		setValue: colorMatchSetValue,
		getValues: colorMatchGetValues,
		clearErrors
	} = useForm<AddColorMatchSchema>({
		resolver: yupResolver(addColorMatchSchema),
		mode: "onSubmit"
	});

	useEffect(() => {
		if (configuration.globals?.finish?.customColor) {
			colorMatchSetValue("paintVendor", {
				code: configuration.globals?.finish?.customColor?.vendorCode,
				description: configuration.globals?.finish?.customColor?.vendorDescription,
				colors: []
			});
			colorMatchSetValue("colorOption", {
				code: configuration.globals?.finish?.customColor?.colorCode,
				description: configuration.globals?.finish?.customColor?.colorDescription
			});
			colorMatchSetValue("colorCode", configuration.globals?.finish?.customColor?.colorDescription ?? null);
		}
	}, [configuration.globals?.finish?.customColor, colorMatchSetValue]);

	const { data: paintVendors } = useGetPaintVendorsQuery(configuration.globals?.productLine?.id ?? "");
	const colors = paintVendors?.vendors?.find(
		(vendor) => vendor.code === configuration.globals?.finish?.customColor?.vendorCode
	)?.colors;

	useEffect(() => {
		if (productData && productData.length > 0) {
			const [matchingProduct] = productData;

			if (matchingProduct?.requiredCategories) {
				matchingProduct?.requiredCategories?.forEach((category) => {
					const selectedItemCategoryValue = selectedEditItem?.current?.requiredCategories?.find(
						(selectedItemCategory) => selectedItemCategory.categoryName === category.name
					);
					category.name &&
						selectedItemCategoryValue &&
						requiredCategoriesSetValue(category.name, selectedItemCategoryValue);
				});
			}
		}
	}, [productData, requiredCategoriesSetValue, selectedEditItem]);

	const quantityValues = Array.from({ length: 99 }, (_, i) => i + 1);
	const quantity = quantityValues.join().split(",");

	const handleMouseDownCapture = (e: SyntheticEvent) => {
		e.stopPropagation();
	};

	const standardDimensions =
		isDimensionValid(selectedEditItem.current?.standardDepth) ||
		isDimensionValid(selectedEditItem.current?.standardHeight) ||
		isDimensionValid(selectedEditItem.current?.standardWidth);

	const generateMenuItems = () => {
		return group.configurations
			.map((configuration) => (
				<MenuItem
					key={configuration.configurationId}
					value={configuration.configurationId ?? ""}
				>
					<EditConfigurationSelect>
						<span>{getAttributes(configuration)}</span>
						<span>{getSubheaderAttributes(configuration)}</span>
					</EditConfigurationSelect>
				</MenuItem>
			))
			.filter((item) => typeof item !== "undefined");
	};

	const handleSaveAction = (
		formValues: FormSchema,
		requiredCategoriesFormValues?: { [key: string]: models["RequiredCategoryValueViewModel"] }
	) => {
		//	figure out which attribute group was selected
		let configToMoveTo = group.configurations.find(
			(config) => config.configurationId === formValues.destinationConfigurationId
		);

		//	find the index of the item in that attribute group since we only have the line number and each group has its own line item array
		let editedItemLineNumberDestinationIndex =
			configToMoveTo?.lineItems?.findIndex((item) => item.lineItemNumber === formValues.editItemsLineNumber) ??
			-1;

		//	findIndex returns negative 1 if the item is not found
		if (editedItemLineNumberDestinationIndex && editedItemLineNumberDestinationIndex === -1) {
			editedItemLineNumberDestinationIndex = configToMoveTo?.lineItems?.length ?? formValues.editItemsLineNumber;
		}

		const { errors, isValid } = getModificationFormErrors(modificationFormValues);

		handleSetModificationFormErrors(errors);

		const modifications: models["PendingModificationViewModel"][] = Array.from(modificationFormValues.values()).map(
			(mod) => ({
				id: mod?.id,
				sku: mod?.sku,
				description: mod?.description,
				lineItemNumber: undefined,
				itemKey: mod?.sku,
				values: mod && "values" in mod && mod?.values !== undefined ? mod.values : undefined,
				importError: undefined
			})
		);

		if (isValid) {
			const requiredValues = Object.values(requiredCategoriesFormValues ?? {});
			const editedLineItem: models["PendingLineItemViewModel"] = {
				...selectedEditItem?.current,
				quantityOrdered: formValues.productSelectQuantity,
				lineItemNumber: 1,
				modifications,
				requiredDimensions: requiredDimensionsValues,
				requiredCategories: requiredValues
			};

			if (
				selectedEditItem.current?.importError ||
				selectedEditItem.current?.modifications?.find((mod) => mod.importError)
			) {
				editedLineItem.importError = {
					message: EditedText
				};
			}

			setLineItemErrorAsEdited(lineItemId);
			handleMoveLineItem(
				editedLineItem,
				editLineItemIndex,
				editedItemLineNumberDestinationIndex,
				configuration.configurationId,
				formValues.destinationConfigurationId
			);

			addEditVisibility();
		}
	};

	const handleDimensionChange = (
		value: number | string,
		description: string | null,
		increment: number,
		maxValue: number,
		minValue: number,
		isUnrestrictedValue: boolean | undefined,
		index: number
	) => {
		const parsedValue = typeof value === "string" ? parseFloat(value) : value;

		const requiredDimensions: models["RequiredDimensionsViewModel"] & models["PendingValueViewModel"] = {
			description: description,
			value: parsedValue,
			increment: increment,
			maxValue: maxValue,
			minValue: minValue,
			isUnrestrictedValue: isUnrestrictedValue
		};

		setRequiredDimensionsValues((prev) =>
			prev.map((dimension, prevIndex) => {
				if (index === prevIndex) return requiredDimensions;
				return dimension;
			})
		);
	};

	const handleFormSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
		event.preventDefault();
		if (isCustomColor) {
			const colorMatchValid = await colorMatchTrigger();
			if (!colorMatchValid) return;
			const { paintVendor, customVendor, colorOption, colorCode } = colorMatchGetValues();
			dispatch(
				newOrderActions.updateColorMatch({
					configurationId: configuration.configurationId,
					colorMatch: {
						vendorCode: paintVendor?.code,
						vendorDescription: customVendor ?? paintVendor?.description,
						colorCode: paintVendor?.code === CustomText ? CustomText : colorOption?.code,
						colorDescription: colorCode ?? colorOption?.description
					}
				})
			);
		}
		const { errors } = getModificationFormErrors(modificationFormValues);
		handleSetModificationFormErrors(errors);
		await requiredCategoriesHandleSubmit((requiredCategoryValues) => {
			handleSubmit((formValues) => handleSaveAction(formValues, requiredCategoryValues))(event);
		})(event);
	};
	const itemToEdit = selectedEditItem?.current ? selectedEditItem.current : {};

	return (
		<form
			onSubmit={handleFormSubmit}
			noValidate
		>
			<EditLineItemHeaderContainer>
				<div data-testid="new-order-draft-table-line-header">{EditLineItemHeader}</div>
				<div
					style={{
						marginTop: "8px"
					}}
				>
					{errorArr.length > 0 && errorArr[0] !== EditedText && (
						<LineItemErrorMessageArr>
							{errorArr?.map((err) => (
								<LineItemErrorMessage
									key={err}
									data-testid="line-item-error-message"
								>
									<ErrorOutline />
									<span>{err}</span>
								</LineItemErrorMessage>
							))}
						</LineItemErrorMessageArr>
					)}
					{errorArr[0] === EditedText && (
						<EditedChip
							label={EditedText}
							icon={<Check />}
							size="small"
						/>
					)}
				</div>
				<EditLineItemHeaderRow>
					<Controller
						name="editItemsLineNumber"
						control={control}
						render={({ field: { onChange, value = null } }) => (
							<AutoComplete
								value={value}
								required
								disableClearable
								onChange={(_event, newValue) => {
									onChange(newValue);
								}}
								onMouseDownCapture={handleMouseDownCapture}
								label={EditLineNumberPlaceholder}
								dataTestId="edit-line-item-row-number"
								options={lineItemCount}
								isError={false}
								errorText=""
							/>
						)}
					/>
					<Header
						key={`${itemToEdit?.sku}-${itemToEdit?.lineItemNumber}-header`}
						item={itemToEdit}
						isEditing={isEditing}
					/>
					<div>
						<span>{EditProductLineLabel}</span>
						<span data-testid="edit-line-item-description">{description}</span>
					</div>
					{isCustomColor ? undefined : (
						<Controller
							name="productSelectQuantity"
							control={control}
							render={({ field: { onChange, value = null } }) => (
								<AutoComplete
									value={value}
									required
									disableClearable
									onChange={(_event, newValue) => {
										onChange(newValue);
									}}
									onMouseDownCapture={handleMouseDownCapture}
									label={AddLineItemQuantityPlaceholder}
									dataTestId="edit-line-item-quantity"
									options={quantity}
									isError={false}
									errorText=""
								/>
							)}
						/>
					)}
				</EditLineItemHeaderRow>

				<EditAddModificationGlobalAttributesWrapper>
					<FormControl
						size="small"
						fullWidth
					>
						<ReplacementReasonAndProblemInputLabel>
							{ChangeGlobalAttributeGroup}
						</ReplacementReasonAndProblemInputLabel>
						<Controller
							name="destinationConfigurationId"
							control={control}
							render={({ field: { onChange, value = null } }) => (
								<Select
									label={ChangeGlobalAttributeGroup}
									data-testid="edit-item-select"
									onChange={onChange}
									value={value ?? ""}
									renderValue={(configurationId: string) => (
										<div className={styles.replacementProblemDropdownOption}>
											<span>
												{getAttributes(
													group?.configurations?.find(
														(config) => config?.configurationId === configurationId
													)
												)}
											</span>
										</div>
									)}
								>
									{generateMenuItems()}
								</Select>
							)}
						/>
					</FormControl>
				</EditAddModificationGlobalAttributesWrapper>

				{standardDimensions && (
					<AddLineItemStandardContainer>
						<div data-testid="edit-line-item-standard-dimensions-header">
							{AddLineItemStandardDimensionsHeader}
						</div>

						<AddLineItemStandardDimensions isAddLineItem={isAddLineItem}>
							{isDimensionValid(selectedEditItem.current?.standardWidth) && (
								<div data-testid="edit-line-item-standard-dimensions-width">
									<span>{AddLineWidthLabel}</span>
									<span>{selectedEditItem.current?.standardWidth}&quot;</span>
								</div>
							)}

							{isDimensionValid(selectedEditItem.current?.standardDepth) && (
								<div data-testid="edit-line-item-standard-dimensions-depth">
									<span>{AddLineDepthLabel}</span>
									<span>{selectedEditItem.current?.standardDepth}&quot;</span>
								</div>
							)}

							{isDimensionValid(selectedEditItem.current?.standardHeight) && (
								<div data-testid="edit-line-item-standard-dimensions-height">
									<span>{AddLineHeightLabel}</span>
									<span>{selectedEditItem.current?.standardHeight}&quot;</span>
								</div>
							)}
						</AddLineItemStandardDimensions>
					</AddLineItemStandardContainer>
				)}

				{((selectedEditItem.current?.requiredCategories &&
					selectedEditItem.current?.requiredCategories?.length !== 0) ||
					(selectedEditItem.current?.requiredDimensions &&
						selectedEditItem.current?.requiredDimensions?.length !== 0)) && (
					<h6 data-testid="edit-line-item-required-specifications-header">{SelectRequiredSpecifications}</h6>
				)}

				<RequiredSpecificationsWrapper>
					{selectedEditItem.current?.requiredDimensions &&
						selectedEditItem.current?.requiredDimensions?.length !== 0 && (
							<>
								<DimensionContainer>
									{selectedEditItem?.current?.requiredDimensions?.map(
										(dimension: any, dimensionIndex) => {
											return (
												dimension && (
													<div key={dimension.id || dimensionIndex}>
														{dimension.isUnrestrictedValue ? (
															<TextField
																key={dimension.id || dimensionIndex}
																type="number"
																label={`Enter ${dimension.description}`}
																defaultValue={dimension.value}
																onChange={(e: ChangeEvent<HTMLInputElement>) =>
																	handleDimensionChange(
																		e.target.value,
																		dimension.description,
																		dimension.increment,
																		dimension.maxValue,
																		dimension.minValue,
																		dimension.isUnrestrictedValue,
																		dimensionIndex
																	)
																}
																required
																inputProps={{
																	step: "any", // Allow any decimal value
																	min: 0
																}}
																size="small"
																fullWidth
																data-testid="edit-line-item-dimensions-unrestricted"
															/>
														) : (
															<AutoComplete
																options={calculateIntervals(
																	dimension.minValue,
																	dimension.maxValue,
																	dimension.increment
																)}
																defaultValue={dimension.value}
																onMouseDownCapture={(e: SyntheticEvent) => {
																	if (
																		e.target instanceof HTMLElement &&
																		e.target.nodeName === "INPUT"
																	) {
																		e.stopPropagation();
																	}
																}}
																onChange={(_, value) =>
																	handleDimensionChange(
																		value,
																		dimension.description,
																		dimension.increment,
																		dimension.maxValue,
																		dimension.minValue,
																		dimension.isUnrestrictedValue,
																		dimensionIndex
																	)
																}
																isLoading={false}
																label={dimension.description ?? ""}
																dataTestId="edit-line-item-dimensions"
																getOptionLabel={(option) => String(option) + '"'}
																required
																isError={false}
																errorText={RequiredDimensionError}
																disableClearable
															/>
														)}

														{!dimension.isUnrestrictedValue && (
															<RequiredDimensionsValues data-testid="edit-line-item-dimension-mix-max">
																{`Min: ${dimension.minValue}", Max: ${dimension.maxValue}"`}
															</RequiredDimensionsValues>
														)}
													</div>
												)
											);
										}
									)}
								</DimensionContainer>
							</>
						)}

					{hasRequiredCategories && isProductQueryFetching && !productData && (
						<ModificationSkeletonContainer>
							<Skeleton height={24} />
							<Skeleton height={24} />
						</ModificationSkeletonContainer>
					)}

					{hasRequiredCategories && productData && productData.length > 0 && (
						<RequiredCategoryWrapper>
							{productData[0]?.requiredCategories?.map((category) => (
								<Controller
									key={category?.name}
									name={category?.name ?? ""}
									control={requiredCategoriesControl}
									rules={{ required: true }}
									render={({ field: { onChange, value = null }, fieldState: { error } }) => (
										<AutoComplete
											onChange={(_e, value) =>
												onChange({ ...value, categoryName: category.name })
											}
											value={value}
											options={category.values ?? []}
											isLoading={false}
											getOptionLabel={(category) => `${category?.sku} - ${category?.description}`}
											label={`Select ${category.name}`}
											dataTestId="edit-line-item-required-category"
											required
											isError={Boolean(error)}
											errorText={RequiredDimensionError}
											disableClearable
										/>
									)}
								/>
							))}
						</RequiredCategoryWrapper>
					)}
				</RequiredSpecificationsWrapper>

				{selectedEditItem.current?.allowModifications && !isCustomColor && (
					<EditAddModificationWrapper>
						<AddModification
							handleClearFieldError={handleSetModificationFormErrors}
							onModificationChange={handleSetModificationFormData}
							selectedEditItem={selectedEditItem}
							errors={modificationFormErrors}
							formValues={modificationFormValues}
							modifications={modifications}
						/>
					</EditAddModificationWrapper>
				)}

				{isCustomColor && (
					<AddColorMatch
						colorMatchType={paintVendors?.type}
						vendors={paintVendors?.vendors ?? []}
						isLoadingVendors={false}
						control={colorMatchControl}
						setValue={colorMatchSetValue}
						clearErrors={clearErrors}
						defaultValues={{
							paintVendor: {
								code: configuration.globals?.finish?.customColor?.vendorCode,
								description: configuration.globals?.finish?.customColor?.vendorDescription,
								colors: colors
							},
							customVendor:
								configuration.globals?.finish?.customColor?.vendorCode === CustomText
									? configuration.globals?.finish?.customColor?.vendorDescription
									: null,
							colorOption: {
								code: configuration.globals?.finish?.customColor?.colorCode,
								description: configuration.globals?.finish?.customColor?.colorDescription
							},
							colorCode: configuration.globals?.finish?.customColor?.colorDescription
						}}
					/>
				)}

				<AddLineItemButtonWrapper isAddLineItem={isAddLineItem}>
					<div>
						<Button
							data-testid="add-line-item-cancel-button"
							variant="text"
							onClick={() => addEditVisibility()}
						>
							{AddLineCancelButton}
						</Button>
					</div>

					<div>
						<Button
							data-testid="add-line-item-save-button"
							variant="contained"
							type="submit"
						>
							{AddLineSaveButton}
						</Button>
					</div>
				</AddLineItemButtonWrapper>
			</EditLineItemHeaderContainer>
		</form>
	);
};
export default EditLineItem;
